import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import Select from "react-select";
import SelectAsync from "react-select/async";
import { apiRequest } from "../../hooks/apiRequest";
import { formatCurrency } from "../../utils/helpers";
import { ESTADOS_CHEQUE } from "../../utils/types";

const Form = ({ cheque, errors, afectarCuentas, isEdit }) => {
    // Un cheque validado o anulado ya movió (o revirtió) dinero: solo se pueden
    // corregir datos que no lo afectan. El API aplica la misma regla.
    const bloqueado = isEdit && ((cheque.status && cheque.status !== "procesando") || cheque.historico);
    const tieneDocumentos = cheque.documentos?.length > 0;

    const [movimientos, setMovimientos] = useState([{
        id: Date.now(),
    }]);

    const [tipo, setTipo] = useState(cheque.tipo ?? "EGRESO");
    const [cuentasContables, setCuentasContables] = useState([]);
    const [numero, setNumero] = useState('');
    const [seguirSecuencia, setSeguirSecuencia] = useState(cheque.id ? (cheque.numeracion ? true : false) : true);
    const [numeraciones, setNumeraciones] = useState([]);
    const [selectedCuenta, setSelectedCuenta] = useState(cheque.cuenta ?{
        label: cheque.cuenta.nombre,
        value: cheque.cuenta.id,
        banco: cheque.cuenta.banco,
    } : null);

    const tipos = [
        {value: "INGRESO", label: "Ingreso"},
        {value: "EGRESO", label: "Egreso"},
    ];

    // Al crear solo se elige entre dejarlo en proceso o validarlo de una vez;
    // rechazar es anular, que existe solo sobre un cheque ya creado.
    const estados = ESTADOS_CHEQUE.filter((e) => ["procesando", "válido"].includes(e.value));

    const getCuentasContables = async () => {
        const cuentas = await apiRequest(null, 'cuentas-contables', 'get');
        if (cuentas.data) {
            setCuentasContables(cuentas.data);
        }
    }

    const getNumeracionSiguiente = async (id) => {
        if (id) {
            const response = await apiRequest(null, 'numeracion-documentos/siguiente/' + id, 'get');
            if (response.data) {
                setNumero(response.data.tipo ? response.data.tipo +  response.data.numero_siguiente : '');
            }
        }
    }

    const addMovimiento = () => {
        setMovimientos([
            ...movimientos,
            {id: Date.now(), monto: 0}
        ]);
    }

    const removeMovimiento = (id) => {
        setMovimientos([
            ...movimientos.filter(m => m.id !== id)
        ]);
    }

    const getNumeraciones = async (banco) => {
        if (banco){
            const table = await apiRequest({}, `numeracion-documentos?buscar=cheque&banco=${banco}`, 'get');
            if (table.data) {
                setNumeraciones(table.data.map(item => ({value: item.id, label: `${item.prefijo} - ${item.nombre}`})));
            }
        } else {
            setNumeraciones([]);
        }
    };

    const getCuentaOptions = async (e) => {
        const table = await apiRequest({}, "cuentas?tipo_pago=cheque&buscar="+e, 'get');
        if (table.data) {
            return table.data.map(item => ({value: item.id, label: item.nombre, banco: item.banco}));
        }
    };

    const getOptions = async (url = null, label = 'nombre', value = 'id') => {
        const table = await apiRequest({}, url, 'get');
        if (table.data) {
            return table.data.map(item => ({value: item[value], label: `${item.codigo || item.numero} - ${item[label]}`}));
        }
    };

    const actualizarMonto = (id, monto) => {
        setMovimientos(movimientos.map(m => {
            if (m.id === id) {
                m.monto = monto;
            }
            return m;
        }));
    }

    useEffect(() => {
        getCuentasContables();
    }, []);

    useEffect(() => {
        if (cheque.movimientos) {
            setMovimientos(cheque.movimientos);
            setNumero(cheque.numero);
        }
    }, [cheque]);

    const totalCreditos = movimientos.reduce((a, p) => {
        return a + parseFloat(p.monto);
    }, 0);

    return (
        <div>
            <div className="row">
                <div className='col-md-4 form-group'>
                    <label className='label form-control-label'>
                        Tipo:
                    </label>
                    <Select
                        isClearable
                        name="tipo"
                        options={tipos}
                        menuPosition='fixed'
                        menuPlacement='auto'
                        isDisabled={isEdit}
                        onChange={e => setTipo(e.value)}
                        value={{
                            label: tipo ?? "EGRESO",
                            value: tipo ?? "EGRESO",
                        }}
                    />
                    {errors.tipo && (
                        <small className='help-blockParams form-Text'>
                            {errors.tipo[0]}
                        </small>
                    )}
                </div>

                <div className='col-md-4 form-group'>
                    <label className='label form-control-label'>
                        Fecha del cheque:
                    </label>
                    <input
                        type='date'
                        className='text-input form-control'
                        id='fecha'
                        name='fecha'
                        readOnly={bloqueado}
                        defaultValue={dayjs(cheque.fecha || undefined).format('YYYY-MM-DD')}
                    />
                    {errors.fecha && (
                        <small className='help-blockParams form-Text'>
                            {errors.fecha[0]}
                        </small>
                    )}
                </div>

                <div className='col-md-4 form-group'>
                    <label className='label form-control-label'>
                        Estado:
                    </label>
                    {isEdit ? (
                        <>
                            <div>
                                <span className={`badge badge-${ESTADOS_CHEQUE.find((e) => e.value === cheque.status)?.badge || "warning"} p-2 uppercase`}>
                                    {cheque.status || "Procesando"}
                                </span>
                            </div>
                        </>
                    ) : (
                        <>
                            <Select
                                isClearable
                                name="status"
                                options={estados}
                                menuPosition='fixed'
                                menuPlacement='auto'
                                defaultValue={estados.find(e => e.value === "procesando")}
                            />
                            {errors.status && (
                                <small className='help-blockParams form-Text'>
                                    {errors.status[0]}
                                </small>
                            )}
                        </>

                    )}
                </div>
            </div>
            <div className='row'>
                {tipo === "EGRESO" ? (
                    <>
                        <div className='col-md-4 form-group'>
                            <label className='label form-control-label'>
                                Cuenta Bancaria:
                            </label>
                            <SelectAsync
                                key="cuenta"
                                name='cuenta_id'
                                placeholder='Seleccionar...'
                                menuPosition='fixed'
                                menuPlacement='auto'
                                cacheOptions
                                defaultOptions
                                isDisabled={bloqueado}
                                defaultValue={cheque.cuenta && ({
                                    label: cheque.cuenta.nombre,
                                    value: cheque.cuenta.id,
                                    banco: cheque.cuenta.banco,
                                })}
                                loadOptions={(e) => getCuentaOptions(e)}
                                onChange={(e) => {
                                    getNumeraciones(e?.banco);
                                    setSelectedCuenta(e);
                                }}
                            />
                            {errors.cuenta_id && (
                                <small className='help-blockParams form-Text'>
                                    {errors.cuenta_id[0]}
                                </small>
                            )}
                        </div>
                        <div className='col-md-8 form-group'>
                            <label className='label form-control-label'>
                            Nombre de chequera:
                            </label>
                            <div className="d-flex">
                                <input type="checkbox" className="m-r-15" onChange={e => setSeguirSecuencia(!seguirSecuencia)} checked={seguirSecuencia} disabled={!selectedCuenta} title="Seguir Secuencia" />
                                <div className="m-r-15" style={{flex: 0.75}}>
                                    <Select
                                        isClearable
                                        name="numeracion_id"
                                        options={numeraciones}
                                        isDisabled={bloqueado || !selectedCuenta || !seguirSecuencia}
                                        menuPosition='fixed'
                                        menuPlacement='auto'
                                        defaultValue={cheque.numeracion && {
                                            label: `${cheque.numeracion.tipo} - ${cheque.numeracion.nombre}`,
                                            value: cheque.numeracion.id,
                                        }}
                                        onChange={ e => {
                                            getNumeracionSiguiente(e?.value);
                                        }}
                                    />
                                </div>
                                <div style={{flex: 1}}>
                                    <input
                                        type='text'
                                        name="numero"
                                        required
                                        readOnly={bloqueado || !selectedCuenta || seguirSecuencia}
                                        className='text-input form-control'
                                        defaultValue={numero}
                                    />
                                    {errors.numero && (
                                        <small className='help-blockParams form-Text'>
                                            {errors.numero[0]}
                                        </small>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className='col-md-6 form-group'>
                            <label className='label form-control-label'>
                                Banco:
                            </label>
                            <input
                                type='text'
                                className='text-input form-control'
                                id='banco'
                                name='banco'
                                required
                                readOnly={bloqueado}
                                defaultValue={cheque.banco}
                            />
                            {errors.banco && (
                                <small className='help-blockParams form-Text'>
                                    {errors.banco[0]}
                                </small>
                            )}
                        </div>
                        <div className='col-md-6 form-group'>
                            <label className='label form-control-label'>
                                Número de Cheque:
                            </label>
                            <input
                                type='text'
                                className='text-input form-control'
                                id='numero'
                                name='numero'
                                required
                                readOnly={bloqueado}
                                defaultValue={cheque.numero}
                            />
                            {errors.numero && (
                                <small className='help-blockParams form-Text'>
                                    {errors.numero[0]}
                                </small>
                            )}
                        </div>
                    </>
                )}
            </div>

            <div className='row'>
                 <div className='col-md-8 form-group'>
                    <label className='label form-control-label'>
                        {tipo === "INGRESO" ? "Cliente:" : "Proveedor:"}
                    </label>
                    <SelectAsync
                        key="proveedor"
                        name='contacto_id'
                        placeholder='Seleccionar...'
                        menuPosition='fixed'
                        menuPlacement='auto'
                        cacheOptions
                        defaultOptions
                        isDisabled={bloqueado || tieneDocumentos}
                        defaultValue={cheque.contacto && ({
                            label: `${cheque.contacto.codigo} - ${cheque.contacto.nombre}`,
                            value: cheque.contacto.id,
                        })}
                        loadOptions={(e) => getOptions("contactos?buscar="+e)}
                    />
                    {errors.contacto_id && (
                        <small className='help-blockParams form-Text'>
                            {errors.contacto_id[0]}
                        </small>
                    )}
                </div>

                <div className='col-md-4 form-group'>
                    <label className='label form-control-label'>
                        Monto:
                    </label>
                    <div className='input-box'>
                        <span className="prefix">$</span>
                        {/* El monto de un cheque con documentos es la suma de sus líneas. */}
                        <input
                            type='text'
                            readOnly={bloqueado || tieneDocumentos}
                            defaultValue={cheque.monto}
                            name='monto'
                        />
                    </div>
                    {errors.monto && (
                        <small className='help-blockParams form-Text'>
                            {errors.monto[0]}
                        </small>
                    )}
                </div>

                <div className='col-md-8 form-group'>
                    <label className='label form-control-label'>
                        Concepto:
                    </label>
                    <input
                        type='text'
                        className='text-input form-control'
                        id='concepto'
                        name='concepto'
                        required
                        readOnly={bloqueado}
                        defaultValue={cheque.concepto}
                    />
                    {errors.concepto && (
                        <small className='help-blockParams form-Text'>
                            {errors.concepto[0]}
                        </small>
                    )}
                </div>

                <div className="col-md-4 form-group">
                    <label className='label form-control-label'>
                        {tipo === "INGRESO" ? "Recibido de:" : "Entregado a:"}
                    </label>
                    <input
                        type='text'
                        className='text-input form-control'
                        id='entregado'
                        name='entregado'
                        required
                        defaultValue={cheque.entregado}
                    />
                    {errors.entregado && (
                        <small className='help-blockParams form-Text'>
                            {errors.entregado[0]}
                        </small>
                    )}
                </div>

                {tipo === "INGRESO" && (
                    <div className='col-md-6 form-group'>
                        <label className='label form-control-label'>
                            Acreditado a cuenta:
                        </label>
                        <SelectAsync
                            key="cuenta"
                            name='cuenta_id'
                            placeholder='Seleccionar...'
                            menuPosition='fixed'
                            menuPlacement='auto'
                            cacheOptions
                            defaultOptions
                            isDisabled={bloqueado}
                            defaultValue={cheque.cuenta && ({
                                label: cheque.cuenta.nombre,
                                value: cheque.cuenta.id,
                                banco: cheque.cuenta.banco,
                            })}
                            loadOptions={(e) => getCuentaOptions(e)}
                            onChange={(e) => {
                                getNumeraciones(e?.banco);
                                setSelectedCuenta(e);
                            }}
                        />
                        {errors.cuenta_id && (
                            <small className='help-blockParams form-Text'>
                                {errors.cuenta_id[0]}
                            </small>
                        )}
                    </div>
                )}
            </div>
            {afectarCuentas && (
                <div>
                    <table className="table table-compact m-t-20">
                        <thead>
                            <tr>
                                <th width="35%">Cuenta Contable</th>
                                <th>Descripción</th>
                                <th width="200">Monto</th>
                                <th width="60"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {movimientos.map((movimiento, index) => (
                                <MovimientoCuenta
                                    key={movimiento.id}
                                    movimiento={movimiento}
                                    index={index}
                                    getOptions={getOptions}
                                    removeMovimiento={removeMovimiento}
                                    errors={errors}
                                    cuentasContables={cuentasContables}
                                    actualizarMonto={actualizarMonto}
                                />
                            ))}
                            <tr>
                                <td>
                                {errors.total_creditos && (
                                    <small className='help-blockParams form-Text'>
                                        {errors.total_creditos[0]}
                                    </small>
                                )}
                                </td>
                                <td className="text-right"><strong>Total</strong></td>
                                <td>
                                    <input type="hidden" name="total_creditos" value={totalCreditos} />
                                    {formatCurrency(totalCreditos)}
                                </td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                    <div className="text-right">
                        <button onClick={addMovimiento} type="button" className="btn">
                            <i className="fa fa-plus m-r-10"></i> Agregar Cuenta Contable
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Form;

const MovimientoCuenta = ({movimiento, index, errors, removeMovimiento, cuentasContables, actualizarMonto}) => {
    return (
        <tr>
            <td>
                <input type="hidden" name={`movimientos_cuentas_contables[${index}][id]`} value={movimiento.id} />
                <Select
                    key={"cuenta" + index}
                    name={`movimientos_cuentas_contables[${index}][cuenta_contable_id]`}
                    placeholder='Seleccionar...'
                    menuPosition='fixed'
                    menuPlacement='auto'
                    defaultValue={movimiento.cuenta?.nombre && ({
                        label: movimiento.cuenta.nombre,
                        value: movimiento.cuenta.id,
                    })}
                    options={cuentasContables.map(c => ({
                        label: `${c.numero} - ${c.nombre}`,
                        value: c.id
                    }))}
                />
                {errors[`movimientos_cuentas_contables.${index}.cuenta_contable_id`] && (
                    <small className='help-blockParams form-Text'>
                        {errors[`movimientos_cuentas_contables.${index}.cuenta_contable_id`][0]}
                    </small>
                )}
            </td>
            <td>
                <div className='input-group'>
                    <input
                        type='text'
                        name={`movimientos_cuentas_contables[${index}][descripcion]`}
                        className='text-input form-control'
                        defaultValue={movimiento.descripcion}
                    />
                </div>
                {errors[`movimientos_cuentas_contables.${index}.descripcion`] && (
                    <small className='help-blockParams form-Text'>
                        {errors[`movimientos_cuentas_contables.${index}.descripcion`][0]}
                    </small>
                )}
            </td>
            <td>
                <div className='input-box'>
                    <span className="prefix">RD$</span>
                    <input
                        type='number'
                        name={`movimientos_cuentas_contables[${index}][monto]`}
                        onChange={(e) => {
                            actualizarMonto(movimiento.id, e.target.value);
                        }}
                        defaultValue={movimiento.monto}
                    />
                </div>
                {errors[`movimientos_cuentas_contables.${index}.monto`] && (
                    <small className='help-blockParams form-Text'>
                        {errors[`movimientos_cuentas_contables.${index}.monto`][0]}
                    </small>
                )}
            </td>
            <td className="text-center">
                <button type="button" className="btn" onClick={() => removeMovimiento(movimiento.id)}>
                    <i className="fa fa-trash"></i>
                </button>
            </td>
        </tr>
    );
}
